import { css } from '@emotion/react';
import { color, Icon, iconSize, Typography } from '@uniquegood/realworld-studio-design';
import { RowFlex, ColumnFlex } from '@/styles';

interface KpiCardProps {
  title: {
    text: string;
    icon?: string;
  };
  value: string | number;
  valueIcon?: string;
  color: string;
  icon?: string;
  isRestricted?: boolean;
  onRestrictedClick?: () => void;
  restrictedValue?: string;
  onClick?: () => void;
}

export default function KpiCard({
  title,
  value,
  valueIcon,
  color: iconColor,
  icon = 'check_solid',
  isRestricted = false,
  onRestrictedClick,
  restrictedValue,
  onClick
}: KpiCardProps) {
  const displayValue = isRestricted ? restrictedValue : value;
  const WrapperTag = isRestricted ? 'button' : 'div';

  return (
    <WrapperTag
      css={WrapperStyle(isRestricted, onClick)}
      onClick={isRestricted ? onRestrictedClick : onClick}
      type={isRestricted ? 'button' : undefined}
      aria-label={isRestricted ? `${title} - 요금제 업그레이드 필요` : undefined}
    >
      <div css={IconWrapperStyle}>
        <Icon icon={icon} size={iconSize.small} color={iconColor} />
      </div>
      <div css={ContentStyle}>
        <div css={TitleRowStyle}>
          <Typography as="dt" type="caption" cssStyle={TitleStyle}>
            {title.text}
          </Typography>
          {title.icon && (
            <Icon
              icon={title.icon}
              size="8px"
              color={color.text_subdued}
              cssStyle={css`
                margin: 0;
              `}
            />
          )}
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 4px;
          `}
        >
          <Typography as="dd" type="displayLarge" cssStyle={ValueStyle}>
            {displayValue}
          </Typography>
          {valueIcon && <Icon icon={valueIcon} size={iconSize.small} color={color.text_subdued} />}
        </div>
      </div>
    </WrapperTag>
  );
}

const WrapperStyle = (isRestricted: boolean, onClick?: () => void) => css`
  ${RowFlex}
  width: 179px;
  height: 84px;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;

  background-color: ${color.surface_default_default};
  border: 1px solid ${color.border_default_subdued};
  border-radius: 16px;
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.07);

  flex: 1;
  min-width: 0;

  /* button reset when used as button */
  text-align: left;
  font: inherit;

  ${(isRestricted || onClick) &&
  css`
    cursor: pointer;
  `}
`;

const IconWrapperStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;
  flex-shrink: 0;

  background-color: ${color.palette_primary_purple_tint_01};
  border-radius: 999px;
`;

const ContentStyle = css`
  ${ColumnFlex}
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const TitleRowStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TitleStyle = css`
  color: ${color.text_subdued};
  margin: 0;
`;

const ValueStyle = css`
  color: color.text_default;
  margin: 0;
  white-space: nowrap;
`;
